import React from 'react';
import useMountedGuard from 'Hooks/useMountedGuard';

const FETCH_STARTING = 'fetch-starting';
const FETCH_SUCCEEDED = 'fetch-succeeded';
const FETCH_FAILED = 'fetch-failed';
const SET_COLLECTION = 'set-collection';

const init = () => {
  return {
    isFetching: true,
    fetchError: null,
    collection: null,
  };
};

function reducer(state, action) {
  switch (action.type) {
    case FETCH_STARTING: {
      return {
        ...state,
        isFetching: true,
        fetchError: null,
      };
    }
    case FETCH_SUCCEEDED: {
      return {
        ...state,
        isFetching: false,
        collection: action.collection,
      };
    }
    case FETCH_FAILED: {
      return {
        ...state,
        isFetching: false,
        fetchError: action.fetchError,
      };
    }
    case SET_COLLECTION: {
      return {
        ...state,
        collection: action.collection,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

/**
 * useRelationship
 *
 * Provides an interface for fetching data via facet rest.
 */
const useRelationship = (relationship, options = {}) => {
  const {
    onError,
  } = options;

  const [state, _dispatch] = React.useReducer(reducer, null, init);
  const { doWithMountedGuard } = useMountedGuard();

  const dispatch = doWithMountedGuard(_dispatch);

  async function fetchRelationship(_relationship) {
    try {
      dispatch({ type: FETCH_STARTING });

      const resp = await _relationship.getRelated();

      dispatch({ type: FETCH_SUCCEEDED, collection: resp });
    } catch (err) {
      dispatch({ type: FETCH_FAILED, fetchError: err });
      if (typeof onError === 'function') {
        onError(err);
      }
    }
  }

  // If the relationship changes, fetch the new data
  React.useEffect(() => {
    const cb = async () => {
      await fetchRelationship(relationship);
    };
    cb();
  }, [relationship]);

  const refreshCollection = React.useCallback(() => {
    return fetchRelationship(relationship);
  }, [state]);

  const setCollection = (coll) => {
    dispatch({ type: SET_COLLECTION, collection: coll });
  };

  return {
    isInitializing: !state.collection && state.isFetching, // If no collection and fetching, then this is initializing
    isInitialized: !!state.collection,
    loading: state.isFetching,
    loadingError: state.fetchError,
    collection: state.collection,
    refreshCollection,
    setCollection,
  };
};

export default useRelationship;
